'use client';
import { Link } from '@/i18n/routing';
import { footerSections } from '@/utils/data';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import { FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { HiOutlineMinus, HiOutlinePlus } from 'react-icons/hi';

import nicholas from '/public/assets/footer.png';
import nicholasWhite from '/public/assets/white-footer-logo.png';

const Footer = () => {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const t = useTranslations('footer');

  const toggleSection = (sectionTitle: string) => {
    setOpenSection(openSection === sectionTitle ? null : sectionTitle);
  };

  return (
    <div className="mx-5 pb-6 md:px-4 md:pt-20 lg:mx-[60px] 2xl:px-0">
      {/* Desktop View */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-1 gap-8 pb-5 md:grid-cols-4 md:pb-20">
          <div>
            <>
              {/* Light mode logo */}
              <Image
                unoptimized={true}
                quality={100}
                src={nicholas}
                alt="Nicholas"
                className="block h-12 w-[82px] cursor-pointer dark:hidden 2xl:max-h-12 2xl:max-w-[82px]"
              />
              {/* Dark mode logo */}
              <Image
                unoptimized={true}
                quality={100}
                src={nicholasWhite}
                alt="Nicholas White"
                className="hidden h-12 w-[82px] cursor-pointer dark:block 2xl:max-h-12 2xl:max-w-[82px]"
              />
            </>
            <div className="pt-3 md:w-36">
              <span className="text-sm text-black dark:text-white">{t('footer_fashion_brand')}</span>
            </div>
            <h2 className="pt-8 text-base font-semibold text-black dark:text-white">{t('footer_follow_us')}</h2>
            <div className="flex cursor-pointer gap-4 pt-4">
              <Link href={'https://www.linkedin.com/'}>
                <FaLinkedin className="rounded-md text-lg text-clr-20 dark:text-white" />
              </Link>
              <Link href={'https://www.instagram.com/'}>
                <FaInstagram className="rounded-md text-lg text-clr-20 dark:text-white" />
              </Link>
              <Link href={'https://www.youtube.com/'}>
                <FaYoutube className="rounded-md text-lg text-clr-20 dark:text-white" />
              </Link>
            </div>
          </div>
          {footerSections.map(section => (
            <div key={section.id}>
              <h2 className="text-base font-semibold text-black dark:text-white">{t(section.title)}</h2>
              <ul className="space-y-2 pt-4 text-sm font-medium text-black dark:text-white">
                {section.path.map((path: { name: string; url: string }) => (
                  <li key={path.name}>
                    <Link href={path.url} className="hover:text-clr-20">
                      {t(path.name)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile View */}
      <div className="pt-8 lg:hidden">
        {/* Logo and Description */}
        <div className="flex flex-col items-center justify-center text-center">
          <>
            <Image
              src={nicholas}
              alt="Nicholas"
              unoptimized
              quality={100}
              className="block h-12 w-[82px] cursor-pointer dark:hidden"
            />
            <Image
              src={nicholasWhite}
              alt="Nicholas White"
              unoptimized
              quality={100}
              className="hidden h-12 w-[82px] cursor-pointer dark:block"
            />
          </>
          <p className="mt-3 text-sm text-black dark:text-white">{t('footer_fashion_brand')}</p>
        </div>

        {/* Social Icons */}
        <h2 className="pt-8 text-base font-semibold text-black dark:text-white">{t('footer_follow_us')}</h2>
        <div className="flex justify-center gap-4 pt-4">
          <FaLinkedin className="rounded-md text-xl text-clr-20 transition-colors hover:text-blue-700 dark:text-white" />
          <FaInstagram className="rounded-md text-xl text-clr-20 transition-colors hover:text-pink-600 dark:text-white" />
          <FaYoutube className="rounded-md text-xl text-clr-20 transition-colors hover:text-red-600 dark:text-white" />
        </div>

        {/* Accordion FAQ Section */}
        <div className="pt-2">
          {footerSections.map(section => (
            <div key={section.id} className="dark:border-gray-700">
              <button
                onClick={() => toggleSection(section.title)}
                className="flex w-full items-center justify-between py-3 text-left text-base font-semibold text-black transition-colors dark:text-white"
              >
                {t(section.title)}
                {openSection === section.title ? (
                  <HiOutlineMinus className="rotate-180 text-xl text-clr-20 transition-transform" />
                ) : (
                  <HiOutlinePlus className="text-xl text-clr-20 transition-transform" />
                )}
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openSection === section.title ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <ul className="space-y-2 text-sm font-medium text-black dark:text-white">
                  {section.path.map((path: { name: string; url: string }) => (
                    <li key={path.name}>
                      <Link href={path.url} className="block transition-all hover:underline">
                        {t(path.name)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-clr-33 pt-8">
        <span className="text-sm text-gray-600 dark:text-white">© ANC {new Date().getFullYear()}</span>
      </div>
    </div>
  );
};

export default Footer;
